import React, { Suspense, useEffect, useState, lazy } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import SkeletonLoader from "./components/loader";
import { Route, Routes, useLocation, useSearchParams } from "react-router-dom";
import Footer from "./components/footer/Footer";
import Header from "./components/header/Header";
import { ToastContainer } from "react-toastify";
// Import CSS optimization utilities
import { purgeUnusedCSS, optimizeYouTubeEmbeds, extractCriticalCSS, measureCSSPerformance } from "./utils/cssPurger";
// Import CSS optimizer component
import CSSOptimizer from "./components/performance/CSSOptimizer";
// Import user actions for campaign tracking
import * as userActions from "./store/actions/user";
// Import GTM tracking utilities
import { trackPageView, initializeGTMTracking } from "./utils/gtm";
import { connect } from "react-redux";

// Lazy load all page components for code splitting
const Home = lazy(() => import("./pages/home/Home"));
const Career = lazy(() => import("./pages/careers/Career"));
const ContactPage = lazy(() => import("./pages/contact/ContactPage"));
const Login = lazy(() => import("./pages/auth/login"));
const PasswordReset = lazy(() => import("./pages/auth/reset"));
const SuccessPage = lazy(() => import("./pages/auth/success-page"));
const SignUp = lazy(() => import("./pages/auth/signup"));
const Privacy = lazy(() => import("./pages/privacy/Privacy"));
const Cookies = lazy(() => import("./pages/cookies/Cookies"));
const Terms = lazy(() => import("./pages/terms/Terms"));
const ScrollTop = lazy(() => import("./common/ScrollTop"));
const Detail = lazy(() => import("./components/detail/Detail"));
const TermsAgreement = lazy(() => import("./pages/termsAgreement/TermsAgreement"));
const SaleAgreement = lazy(() => import("./pages/saleAgreement/SaleAgreement"));
const DigitalAsset = lazy(() => import("./pages/digitalAsset/DigitalAsset"));
const CustodialTerms = lazy(() => import("./pages/custodialTerms/CustodialTerms"));
const Custody = lazy(() => import("./pages/custody/Custody"));
const AccountDeletion = lazy(() => import("./pages/accountDeletion/AccountDeletion"));

function App({trackCampaignQRCode}) {
  const [overlay, setOverlay] = useState(false);
  const [searchParams] = useSearchParams();

  const location = useLocation();
  
  // Helper function to get readable page names for analytics
  const getPageName = (pathname) => {
    const pageNames = {
      '/': 'Home',
      '/careers': 'Careers',
      '/contact': 'Contact',
      '/login': 'Login',
      '/signup': 'Sign Up',
      '/privacy': 'Privacy Policy',
      '/cookies': 'Cookies Policy',
      '/terms': 'Terms of Service',
      '/terms-agreement': 'Terms Agreement',
      '/sale-agreement': 'Sale Agreement',
      '/digital-asset': 'Digital Asset',
      '/custodial-terms': 'Custodial Terms',
      '/custody': 'Custody',
      '/account-deletion': 'Account Deletion'
    };
    
    return pageNames[pathname] || pathname.replace('/', '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };
  
  useEffect(() => {
    AOS.init({
      once: false,
      offset: 100,
    });
    
    // Performance optimization: CSS purging and YouTube optimization
    const optimizePerformance = async () => {
      try {
        // Wait for DOM to be fully loaded
        if (document.readyState === 'complete') {
          await runPerformanceOptimizations();
        } else {
          window.addEventListener('load', runPerformanceOptimizations);
        }
      } catch (error) {
        console.log('Performance optimization error:', error);
      }
    };
    
    optimizePerformance();
    
    // Cleanup
    return () => {
      window.removeEventListener('load', runPerformanceOptimizations);
    };
  }, []);

  // Campaign tracking - monitor route changes and ref parameters
  useEffect(() => {
    const trackCampaign = async () => {
      const refParam = searchParams.get("ref");

      // Only call API if ref parameter exists and we're on a valid route
      if (refParam && (location.pathname=="/" || location.pathname=="/signup")) {
        try {
          const response = await trackCampaignQRCode(refParam);
          console.log("Campaign tracking successful:", response.data);
        } catch (error) {
          console.error("Campaign tracking failed:", error);
        }
      }
    };

    trackCampaign();
  }, [location.pathname, searchParams]);

  // GTM Route Tracking - track page views when routes change
  useEffect(() => {
    // Track page view using GTM utility
    trackPageView({
      custom_page_name: getPageName(location.pathname),
      referrer: document.referrer,
      user_agent: navigator.userAgent
    });
    
    // Also push to gtag if available (for Google Analytics)
    if (window.gtag) {
      window.gtag('config', 'G-P52P92Y3EH', {
        page_path: location.pathname + location.search + location.hash
      });
    }
  }, [location.pathname, location.search, location.hash]);

  // Initialize GTM tracking features
  useEffect(() => {
    const cleanupGTM = initializeGTMTracking();
    
    return () => {
      if (cleanupGTM) cleanupGTM();
    };
  }, []);
  
  // Performance optimization function
  const runPerformanceOptimizations = async () => {
    try {
      // Measure initial CSS performance
      const initialPerformance = measureCSSPerformance();
      
      // Optimize YouTube embeds to prevent unused CSS loading
      const optimizedEmbeds = optimizeYouTubeEmbeds();
      console.log(`🎥 Optimized ${optimizedEmbeds} YouTube embeds`);
      
      // Extract critical CSS for above-the-fold content
      const criticalCSS = extractCriticalCSS();
      console.log(`⚡ Found ${criticalCSS.length} critical CSS classes`);
      
      // Analyze unused CSS
      const purgeResults = purgeUnusedCSS();
      console.log(`🧹 CSS purge analysis complete. Potential savings: ${purgeResults.estimatedSavings} KiB`);
      
      // Measure performance after optimization
      setTimeout(() => {
        const finalPerformance = measureCSSPerformance();
        const improvement = initialPerformance - finalPerformance;
        console.log(`📈 Performance improved by ${improvement.toFixed(2)}ms`);
      }, 1000);
      
    } catch (error) {
      console.log('Performance optimization failed:', error);
    }
  };

  const isAuthPage = [
    "/signup",
    "/login",
    "/forgot",
    "/check-email",
    "/reset-password",
    "/verify-email",
    "/two-factor",
    "/authenticator",
    "/success",
  ].includes(location.pathname);

  return (
    <>
      {/* CSS Performance Optimizer - Hidden component */}
      <CSSOptimizer />
      
      {isAuthPage ? (
        <Suspense fallback={<SkeletonLoader />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/reset-password" element={<PasswordReset />} />
            <Route path="/success" element={<SuccessPage />} />
          </Routes>
          <ToastContainer />
        </Suspense>
      ) : (
        <>
          <Header setOverlay={setOverlay} />
          <div className="main-content">
            <Suspense fallback={<SkeletonLoader />}>
              <ScrollTop />
              <Routes>
                <Route
                  path="/"
                  element={
                    <Suspense fallback={<SkeletonLoader />}>
                      <Home />
                    </Suspense>
                  }
                />
                <Route
                  path="/careers"
                  element={
                    <Suspense fallback={<SkeletonLoader />}>
                      <Career />
                    </Suspense>
                  }
                />
                <Route
                  path="/blog-detail/:id"
                  element={
                    <Suspense fallback={<SkeletonLoader />}>
                      <Detail />
                    </Suspense>
                  }
                />
                <Route
                  path="/contact-us"
                  element={
                    <Suspense fallback={<SkeletonLoader />}>
                      <ContactPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/privacy-policy"
                  element={
                    <Suspense fallback={<SkeletonLoader />}>
                      <Privacy />
                    </Suspense>
                  }
                />
                <Route
                  path="/terms-of-use"
                  element={
                    <Suspense fallback={<SkeletonLoader />}>
                      <Terms />
                    </Suspense>
                  }
                />
                <Route
                  path="/conditional-sale-agreement"
                  element={
                    <Suspense fallback={<SkeletonLoader />}>
                      <SaleAgreement />
                    </Suspense>
                  }
                />
                <Route
                  path="/Digital-Asset-Pledge-Terms"
                  element={
                    <Suspense fallback={<SkeletonLoader />}>
                      <DigitalAsset />
                    </Suspense>
                  }
                />
                <Route
                  path="/Custodial-Services-Terms"
                  element={
                    <Suspense fallback={<SkeletonLoader />}>
                      <CustodialTerms />
                    </Suspense>
                  }
                />
                <Route
                  path="/terms-of-use-customer-conditional-sale-agreement"
                  element={
                    <Suspense fallback={<SkeletonLoader />}>
                      <TermsAgreement />
                    </Suspense>
                  }
                />
                <Route
                  path="/custody"
                  element={
                    <Suspense fallback={<SkeletonLoader />}>
                      <Custody />
                    </Suspense>
                  }
                />
                <Route
                  path="/cookie-settings"
                  element={
                    <Suspense fallback={<SkeletonLoader />}>
                      <Cookies />
                    </Suspense>
                  }
                />
                <Route
                  path="/account-deletion"
                  element={
                    <Suspense fallback={<SkeletonLoader />}>
                      <AccountDeletion />
                    </Suspense>
                  }
                />
              </Routes>
            </Suspense>
          </div>
          <Footer />
          <ToastContainer />
        </>
      )}
    </>
  );
}

export default connect(null, userActions)(App);
